import { component } from "picoapp";
import {
  getFirstFocusableElement,
  getLastFocusableElement,
} from "../../utilities";
import { checkCanFocusTrap } from "../../../scripts/lib/focus-trap-utils";
import { type Context } from "../../state";
import { TablescapeEvent, TablescapeContext } from "@tablescape/context";

export const mobileNavigation = component<Context>(async (node, context) => {
  // Elements
  const topNavHamburgerElement = document.querySelector<HTMLButtonElement>(
    ".js-hamburger-button"
  );
  const topNavCloseElement =
    node.querySelector<HTMLButtonElement>(".js-close-button");
  const mobileMenu = node.querySelector<HTMLElement>(".js-mobile-menu");
  const mobileMenuHeader = node.querySelector<HTMLElement>(
    ".js-mobile-menu-header"
  );
  const mobileMenuHeaderLastFocusElement = mobileMenuHeader
    ? getLastFocusableElement(mobileMenuHeader)
    : null;

  const dropdownLevelOne = node.querySelector<HTMLElement>(
    ".js-dropdown-level-1"
  );
  const dropdownLevelOneLinkElements = node.querySelectorAll<HTMLElement>(
    ".js-dropdown-level-1-link"
  );
  const dropdownLevelTwoCloseButtonElements =
    node.querySelectorAll<HTMLButtonElement>(".js-dropdown-level-2-close");
  const bodyElement = document.querySelector("body");
  const cartButtonElements = node.querySelectorAll(".js-cart-button");

  // State
  let mobileNavOpen = false;
  let activeLevelTwoDropdown: string | null = null;
  const dropdownLevelOneLastFocusElement = dropdownLevelOne
    ? getLastFocusableElement(dropdownLevelOne)
    : null;
  let mobileNavLastFocusableElement = dropdownLevelOneLastFocusElement;

  // Handlers
  const cartClickHandler = (e: Event) => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_CART);
    closeMobileDropdownHandler(e);
  };

  const handleMobileMenuKeyboardEvents = (e: KeyboardEvent) => {
    if (mobileNavOpen) {
      if (e.key === "Tab") {
        if (!e.shiftKey) {
          if (document.activeElement === mobileNavLastFocusableElement) {
            topNavCloseElement?.focus();
            e.preventDefault();
          }

          if (document.activeElement === mobileMenuHeaderLastFocusElement) {
            const currentLevelTwoDropdown = node.querySelector<HTMLElement>(
              `#js-mobile-menu-${activeLevelTwoDropdown}`
            );
            if (currentLevelTwoDropdown) {
              const firstFocusable = getFirstFocusableElement(
                currentLevelTwoDropdown
              );
              firstFocusable?.focus();
            } else if (dropdownLevelOne) {
              const firstFocusable = getFirstFocusableElement(dropdownLevelOne);
              firstFocusable?.focus();
            }
            e.preventDefault();
          }
        } else {
          if (document.activeElement === topNavCloseElement) {
            mobileNavLastFocusableElement?.focus();
            e.preventDefault();
          }
        }
      }

      if (e.key === "Escape") {
        if (activeLevelTwoDropdown) {
          closeMobileDropdownLevelTwoHandler(e);
        } else {
          closeMobileDropdownHandler(e);
        }
      }
    }
  };

  const openMobileDropdownHandler = async () => {
    if (topNavHamburgerElement) {
      topNavHamburgerElement.ariaExpanded = "true";
    }
    node.classList.remove("invisible");
    node.classList.remove("opacity-0");
    node.classList.add("z-max");
    node.classList.remove("-z-10");

    if (mobileMenu) {
      mobileMenu.classList.remove("invisible");
      mobileMenu.classList.add("left-0");
      mobileMenu.classList.remove("left-full");
      mobileMenu.classList.remove("opacity-0");
    }

    if (bodyElement) {
      bodyElement.dataset.scrollY = window.scrollY.toString();
      bodyElement.classList.add("fixed", "overflow-y-hidden");
    }

    try {
      await checkCanFocusTrap([node], function (trapContainer) {
        return getComputedStyle(trapContainer).visibility !== "hidden";
      });

      topNavCloseElement?.focus();
      mobileNavOpen = true;
    } catch (e) {
      console.error(e);
    }
  };

  const closeMobileDropdownHandler = (e: Event) => {
    if (topNavHamburgerElement) {
      topNavHamburgerElement.ariaExpanded = "false";
    }
    if (bodyElement) {
      bodyElement.classList.remove("fixed", "overflow-y-hidden");
      window.scroll({ top: parseInt(bodyElement.dataset.scrollY || "0") });
      bodyElement.removeAttribute("data-scroll-y");
    }
    closeMobileDropdownLevelTwoHandler(e);

    if (mobileMenu) {
      mobileMenu.classList.add("left-full");
      mobileMenu.classList.remove("left-0");
      mobileMenu.classList.add("opacity-0");
      mobileMenu.classList.add("invisible");
    }

    node.classList.add("opacity-0");
    node.classList.add("-z-10");
    node.classList.remove("z-max");
    node.classList.add("invisible");

    topNavHamburgerElement?.focus();
    mobileNavOpen = false;
  };

  const openMobileDropdownLevelTwoHandler = async (e: Event) => {
    e.preventDefault();

    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }

    const targetId = e.currentTarget.dataset.levelTwoId;

    if (!targetId) {
      return;
    }

    const dropdownLevelTwo = node.querySelector<HTMLElement>(
      `#js-mobile-menu-${targetId}`
    );

    if (!dropdownLevelTwo) {
      return;
    }

    const closeButton = dropdownLevelTwo.querySelector<HTMLElement>(
      ".js-dropdown-level-2-close"
    );

    if (dropdownLevelOne) {
      dropdownLevelOne.classList.add("invisible");
      dropdownLevelOne.classList.add("-left-full");
      dropdownLevelOne.classList.add("opacity-0");
    }

    dropdownLevelTwo.classList.remove("left-full");
    dropdownLevelTwo.classList.add("left-0");
    dropdownLevelTwo.classList.remove("opacity-0");
    dropdownLevelTwo.classList.remove("invisible");

    try {
      await checkCanFocusTrap([dropdownLevelTwo], function (trapContainer) {
        return (
          getComputedStyle(trapContainer).visibility !== "hidden" &&
          parseInt(getComputedStyle(trapContainer).left) === 0
        );
      });

      mobileNavLastFocusableElement = getLastFocusableElement(dropdownLevelTwo);
      if (closeButton) {
        closeButton.focus();
      }
      activeLevelTwoDropdown = targetId;
    } catch (error) {
      console.error(error);
    }
  };

  const closeMobileDropdownLevelTwoHandler = async (e: Event) => {
    e.preventDefault();
    const dropdownLevelTwo = node.querySelector<HTMLElement>(
      `#js-mobile-menu-${activeLevelTwoDropdown}`
    );

    if (!dropdownLevelTwo) return;

    if (dropdownLevelOne) {
      dropdownLevelOne.classList.remove("invisible");
      dropdownLevelOne.classList.remove("-left-full");
      dropdownLevelOne.classList.remove("opacity-0");
    }

    dropdownLevelTwo?.classList.remove("left-0");
    dropdownLevelTwo?.classList.add("left-full");
    dropdownLevelTwo?.classList.add("opacity-0");
    dropdownLevelTwo?.classList.add("invisible");

    try {
      if (dropdownLevelOne) {
        await checkCanFocusTrap([dropdownLevelOne], function (trapContainer) {
          return (
            getComputedStyle(trapContainer).visibility !== "hidden" &&
            parseInt(getComputedStyle(trapContainer).left) === 0
          );
        });
      }

      mobileNavLastFocusableElement = dropdownLevelOneLastFocusElement;
      const lastFocusedElement = node.querySelector<HTMLElement>(
        `.js-dropdown-level-1-link[data-level-two-id="${activeLevelTwoDropdown}"]`
      );
      if (lastFocusedElement) {
        lastFocusedElement.focus();
      }
      activeLevelTwoDropdown = null;
    } catch (error) {
      console.error(error);
    }
  };

  // Setup Listeners
  node.addEventListener("keydown", handleMobileMenuKeyboardEvents);
  topNavHamburgerElement?.addEventListener("click", openMobileDropdownHandler);
  topNavCloseElement?.addEventListener("click", closeMobileDropdownHandler);

  dropdownLevelOneLinkElements.forEach((link) =>
    link.addEventListener("click", openMobileDropdownLevelTwoHandler)
  );
  dropdownLevelTwoCloseButtonElements.forEach((button) =>
    button.addEventListener("click", closeMobileDropdownLevelTwoHandler)
  );

  cartButtonElements.forEach((icon) => {
    icon.addEventListener("click", cartClickHandler);
  });

  context.on("cart:updated", (payload) => {
    if (payload.item_count > 0) {
      cartButtonElements.forEach((button) => {
        const cartBubble = button.querySelector(".js-cart-bubble");
        const cartCount = button.querySelector(".js-cart-count");

        if (cartBubble) {
          cartBubble.classList.remove("hidden");
        }

        if (cartCount && cartCount.children[0]) {
          cartCount.children[0].innerHTML = payload.item_count.toString();
        }
      });
    } else {
      cartButtonElements.forEach((button) => {
        const cartBubble = button.querySelector(".js-cart-bubble");
        if (cartBubble) {
          cartBubble.classList.add("hidden");
        }
      });
    }
  });

  return () => {
    node.removeEventListener("keydown", handleMobileMenuKeyboardEvents);
    topNavHamburgerElement?.removeEventListener(
      "click",
      openMobileDropdownHandler
    );
    topNavCloseElement?.removeEventListener(
      "click",
      closeMobileDropdownHandler
    );

    dropdownLevelOneLinkElements.forEach((link) =>
      link.removeEventListener("click", openMobileDropdownLevelTwoHandler)
    );
    dropdownLevelTwoCloseButtonElements.forEach((button) =>
      button.removeEventListener("click", closeMobileDropdownLevelTwoHandler)
    );
    cartButtonElements.forEach((button) => {
      button.removeEventListener("click", cartClickHandler);
    });
  };
});
